/**
 * The one switch for the image editor's bulk work (owner 2026-10-05: "I want everything on P0 and
 * single flag turns on all"). `featureFlags.imageEditorBulk` (top-level settings.featureFlags,
 * typed in services/settings.service.ts) gates the /bulk-image-resizer landing, EPS/RAW intake, the
 * server path for heavy formats, per-file crop and the preparing/failure states (PLAN.md C1/C2).
 * Flag off = today's live v2 behaviour exactly: every helper here returns today's value for `false`.
 * Pure; tested in bulk-gate.spec.ts.
 */
import type { FeatureFlags } from '../../utils/request.utils';
import type { LandingSpec } from './routing';
import { emptyStateCopy } from './routing';

export const BULK_SLUG = 'bulk-image-resizer';

/** ON only when the flag is literally `true`; missing/undefined/anything else = off. */
export function isBulkOn(flags: FeatureFlags | null | undefined): boolean {
    return (flags as { imageEditorBulk?: unknown } | null | undefined)?.imageEditorBulk === true;
}

/** Extensions the editor opens today (design doc §4). Order kept: it is the flag-off contract. */
const BROWSER_EXTENSIONS = ['jpg', 'jpeg', 'jfif', 'png', 'apng', 'webp', 'gif', 'bmp', 'tiff', 'tif', 'ico', 'avif', 'heic', 'heif', 'psd', 'ppm'];
const TODAY_ACCEPT_ATTR = 'image/*,.heic,.heif,.tif,.tiff,.psd,.ppm,.ico,.avif';

/** EPS + the RAW extensions codecs/detect.ts maps to 'eps'/'raw' (its table is private; the spec
 *  checks every entry here against detectFormat so the two cannot drift silently). */
const SERVER_ONLY_EXTENSIONS = [
    'eps', 'epsf', 'epsi',
    '3fr', 'arw', 'bay', 'cr2', 'cr3', 'crw', 'dcr', 'dng', 'erf', 'fff', 'gpr', 'iiq', 'k25', 'kdc',
    'mef', 'mos', 'mrw', 'nef', 'nrw', 'orf', 'pef', 'raf', 'raw', 'rw2', 'rwl', 'sr2', 'srf', 'srw', 'x3f',
];

export function acceptedExtensions(bulk: boolean): string[] {
    return bulk ? [...BROWSER_EXTENSIONS, ...SERVER_ONLY_EXTENSIONS] : [...BROWSER_EXTENSIONS];
}

/** `accept` attribute for the pickers. */
export function acceptAttr(bulk: boolean): string {
    return bulk ? `${TODAY_ACCEPT_ATTR},${SERVER_ONLY_EXTENSIONS.map(e => '.' + e).join(',')}` : TODAY_ACCEPT_ATTR;
}

const PASTE_TODAY = /\.(heic|heif|tif|tiff|psd|ppm|ico|avif)$/i;
const PASTE_SERVER = new RegExp(`\\.(${SERVER_ONLY_EXTENSIONS.join('|')})$`, 'i');

/** Clipboard filter (Ctrl/⌘+V). */
export function pasteAccepts(f: File, bulk: boolean): boolean {
    return f.type.startsWith('image/') || PASTE_TODAY.test(f.name) || (bulk && PASTE_SERVER.test(f.name));
}

export interface LandingCopy { heading: string; sub: string; batchFirst: boolean }

/**
 * H1 + subtitle for the empty state: the page's own SSR `page_seo.title` / `page_seo.description` on every slug,
 * /bulk-image-resizer included (xconvert-web-ssr page-data-overrides/<slug>.json is the one writer). Source-of-truth
 * audit 2026-10-10: the bulk branch re-typed bulk-image-resizer.json's page_seo, and its flag-off "Image Resizer"
 * branch (a copy of image-resizer.json) was unreachable since the flag-invariant redesign (editor-root passes the
 * slug's batchFirst as `bulk`); both removed. `${verb} ${noun}` is only the derived label for a page without page data.
 */
export function landingCopy(l: LandingSpec, bulk: boolean, pageTitle: string, pageDescription: string): LandingCopy {
    const copy = emptyStateCopy(l);
    return { heading: pageTitle || `${copy.verb} ${copy.noun}`, sub: pageDescription, batchFirst: !!l.batchFirst && bulk };
}

// The drop-zone formats line lives in routing.ts as the single constant EMPTY_FORMATS_LINE (P0 QA
// round 2 redesign): the old flag-dependent formatsLine(bulk) swapped text after hydration (CLS) and
// claimed server formats were edited in the browser. Do not reintroduce a flag-dependent variant.

const extOfName = (name: string) => (/\.([a-z0-9]+)$/i.exec(name)?.[1] || '').toLowerCase();
const isEpsOrRaw = (name: string) => SERVER_ONLY_EXTENSIONS.includes(extOfName(name));

/**
 * Files the editor must not take in even though the browser types them image/* (J12-1, J12-8): with the
 * flag off EPS and RAW cannot be opened (Linux types .eps image/x-eps, so it slipped past the type check and
 * failed later with a generic "can't be opened" panel and no way on); SVG is never an editor input in
 * either state. They are skipped at intake with a link to the converter that does open them.
 */
export function blockedAtIntake(f: { name: string; type?: string }, bulk: boolean): boolean {
    const ext = extOfName(f.name);
    if (ext === 'svg' || /svg/i.test(f.type || '')) return true;
    if (!bulk && (SERVER_ONLY_EXTENSIONS.includes(ext) || /(^image\/(x-)?eps|postscript)/i.test(f.type || ''))) return true;
    return false;
}

export interface SkipLink { href: string; label: string }

/** RAW extensions with their own converter page (ssr-data/build/convert-<ext>-to-jpg.json, 2026-10-07). */
const RAW_CONVERTERS = ['3fr', 'arw', 'cr2', 'cr3', 'crw', 'dcr', 'dng', 'erf', 'mos', 'mrw', 'nef', 'orf', 'pef', 'raf', 'rw2', 'x3f'];

/** The converter that opens a file this editor skipped, or null when there is no better page. */
export function converterFor(name: string, bulk: boolean): SkipLink | null {
    const ext = extOfName(name);
    if (ext === 'svg') return { href: '/convert-svg-to-png', label: 'Convert SVG to PNG' };
    if (ext === 'pdf') return { href: '/convert-pdf-to-jpg', label: 'Convert PDF to JPG' };
    if (bulk) return null;
    if (['eps', 'epsf', 'epsi'].includes(ext)) return { href: '/convert-eps-to-png', label: 'Convert EPS to PNG' };
    if (RAW_CONVERTERS.includes(ext)) return { href: `/convert-${ext}-to-jpg`, label: `Convert ${ext.toUpperCase()} to JPG` };
    if (isEpsOrRaw(name)) return { href: '/convert-image-to-jpg', label: 'Convert to JPG' };
    return null;
}

/**
 * Why chosen files were not added. The hint after the format list names only what fits the skipped
 * files (J8-11, J11-12): a PDF is pointed at PDF to JPG, never at the RAW / EPS converters.
 */
export function skippedMessage(what: string, bulk: boolean, names: string[] = []): string {
    const list = bulk
        ? `Skipped ${what}: this editor opens JPG, PNG, WebP, GIF, BMP, TIFF, HEIC, AVIF, ICO, PSD, PPM, EPS and RAW.`
        : `Skipped ${what}: this editor opens JPG, PNG, WebP, GIF, BMP, TIFF, HEIC, AVIF, ICO, PSD and PPM.`;
    const hints: string[] = [];
    if (!bulk && names.some(isEpsOrRaw)) hints.push('Convert RAW and EPS files to JPG or PNG first, then edit the result here.');
    if (names.some(n => extOfName(n) === 'pdf')) hints.push('For a PDF, convert its pages to JPG first.');
    if (names.some(n => extOfName(n) === 'svg')) hints.push('For an SVG, convert it to PNG first.');
    return [list, ...hints].join(' ');
}

/** Same file chosen twice (name, size and modified time all equal): not added again (critic-6). */
export function sameFile(a: File, b: File): boolean {
    return a === b || (a.name === b.name && a.size === b.size && a.lastModified === b.lastModified);
}
export function duplicateMessage(names: string[]): string {
    return names.length === 1 ? `${names[0]} is already in the batch, so it was not added again.`
        : `${names.length} files are already in the batch, so they were not added again.`;
}

/** Reload / close / Back with photos open (regression-5). */
export const LEAVE_WARNING = 'Leave the editor? Your photos and edits are not saved and will be lost.';

const MIME_LABEL: Record<string, string> = { 'image/jpeg': 'JPG', 'image/png': 'PNG', 'image/webp': 'WebP', 'image/avif': 'AVIF', 'image/gif': 'GIF', 'image/bmp': 'BMP' };

/**
 * A file the editor could not read or write, said in plain words (formats-12): an empty file says it is
 * empty; the "works with" list matches what this page really opens (EPS/RAW with the flag on); no MIME
 * types and no "a image/jpeg".
 */
export function friendlyFailure(file: { name: string; size: number }, message: string, bulk: boolean): string {
    if (file.size === 0) return `${file.name} is empty (0 bytes): there is no picture in it.`;
    if (/isn't an image the editor can open/.test(message)) {
        return `${file.name} isn't an image the editor can open. ${bulk ? 'It works with JPG, PNG, WebP, GIF, BMP, TIFF, ICO, AVIF, HEIC, PSD, PPM, EPS and RAW.' : 'It works with JPG, PNG, WebP, GIF, BMP, TIFF, ICO, AVIF, HEIC, PSD and PPM.'}`;
    }
    const w = /could not write an? (image\/[\w+.-]+) file/.exec(message);
    if (w) return `The browser could not write this ${MIME_LABEL[w[1]] ?? 'image'} file. It may be too large for this browser: try a smaller size.`;
    return message;
}
